import type { components } from "@/api/schema";
import { moneyFromString, parseMoneyAmount } from "@/shared/lib/money";

/**
 * Framing rules for the Performance tab (spec §5.1, §5.3, §5.4, §4.4).
 * Pure: every branch of the active-vs-finished matrix is decided here and
 * unit-tested; components only render what these return. Returned i18n keys
 * are relative to the `promotion` namespace.
 */

type PromotionStatus = components["schemas"]["Promotion"]["status"];

export type PerformanceTab = "setup" | "performance";
export type KpiKey = "orders" | "gmv" | "abv";

export interface I18nLine {
  key: string;
  values: Record<string, string | number>;
}

export const DASH = "—";
const DAY_MS = 86_400_000;
const BKK = "Asia/Bangkok";

/** Ended = `finish` or `archived`; archived reads exactly like finished. */
function isEnded(status: PromotionStatus | undefined): boolean {
  return status === "finish" || status === "archived";
}

export function hasPerformance(status: PromotionStatus | undefined): boolean {
  return status === "active" || isEnded(status);
}

export function defaultTab(status: PromotionStatus | undefined): PerformanceTab {
  return isEnded(status) ? "performance" : "setup";
}

/** The tab actually shown: the URL wins, except where Performance doesn't exist. */
export function resolveTab(
  status: PromotionStatus | undefined,
  requested?: PerformanceTab,
): PerformanceTab {
  if (!hasPerformance(status)) return "setup";
  return requested ?? defaultTab(status);
}

/** BE money (2-dp baht string, e.g. "2403.07") → "฿2,403.07" via the shared formatter. */
export function formatMoney(amount: string | null | undefined): string {
  return amount === null || amount === undefined || amount === "" ? DASH : moneyFromString(amount);
}

export function formatUplift(pct: number): string {
  return `${pct > 0 ? "+" : ""}${pct.toFixed(1)}%`;
}

export function formatShare(pct: number): string {
  return `${Math.round(pct)}%`;
}

export function formatAsOf(iso: string): string {
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZone: BKK,
  }).format(new Date(iso));
}

export function formatClock(iso: string): string {
  return new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZone: BKK,
  }).format(new Date(iso));
}

export interface PeriodInfo {
  totalDays: number;
  /** 1-based day of the period, clamped to [1, totalDays]. */
  day: number;
  daysLeft: number;
}

export function periodInfo(startIso: string, endIso: string, now: Date): PeriodInfo {
  const start = Date.parse(startIso);
  const totalDays = Math.max(1, Math.ceil((Date.parse(endIso) - start) / DAY_MS));
  const day = Math.min(totalDays, Math.max(1, Math.floor((now.getTime() - start) / DAY_MS) + 1));
  return { totalDays, day, daysLeft: totalDays - day };
}

/** Header period suffix: "day 4 of 6" while active, "6 days" otherwise. */
export function periodCaption(status: PromotionStatus, info: PeriodInfo): I18nLine {
  return status === "active"
    ? { key: "performance.period_day_of", values: { day: info.day, total: info.totalDays } }
    : { key: "performance.period_days", values: { n: info.totalDays } };
}

export interface Banner extends I18nLine {
  intent: "warning" | "info";
}

/** §5.3 banner row. The running banner's `time` (as_of clock) is added by the caller. */
export function bannerFor(
  status: PromotionStatus,
  daysLeft: number,
  inTransit: number,
): Banner | null {
  if (status === "active") {
    return { intent: "warning", key: "performance.banner_running", values: { n: daysLeft } };
  }
  if (isEnded(status) && inTransit > 0) {
    return { intent: "info", key: "performance.banner_in_transit", values: { n: inTransit } };
  }
  return null;
}

export function kpiSubLabel(status: PromotionStatus, key: KpiKey): string {
  return status === "active" ? "performance.kpi.so_far" : `performance.kpi.${key}_caption`;
}

export function upliftDisplay(
  pct: number | null | undefined,
  baselineAbv: string | null | undefined,
): { value: string; caption: I18nLine } {
  if (pct === null || pct === undefined) {
    return { value: DASH, caption: { key: "performance.kpi.uplift_insufficient", values: {} } };
  }
  return {
    value: formatUplift(pct),
    caption: {
      key: "performance.kpi.uplift_caption",
      values: { baseline: formatMoney(baselineAbv) },
    },
  };
}

export function giftsCaption(
  status: PromotionStatus,
  gifts: { unused?: number | null; on_pace?: number | null },
): I18nLine | null {
  if (isEnded(status) && gifts.unused !== null && gifts.unused !== undefined) {
    return {
      key: "performance.kpi.gifts_unused",
      values: { n: gifts.unused.toLocaleString("en-US") },
    };
  }
  if (status === "active" && gifts.on_pace !== null && gifts.on_pace !== undefined) {
    return {
      key: "performance.kpi.gifts_on_pace",
      values: { n: gifts.on_pace.toLocaleString("en-US") },
    };
  }
  return null;
}

/** §4.4 filename, built client-side when Content-Disposition is unreadable. */
export function exportFilename(name: string, status: PromotionStatus, date: Date): string {
  const tag = isEnded(status) ? "FINAL" : "PARTIAL";
  const day = new Intl.DateTimeFormat("en-CA", { timeZone: BKK }).format(date); // YYYY-MM-DD
  return `${name.replace(/[\\/:*?"<>|]/g, "_")}_performance_${tag}_${day}.csv`;
}

/** GMV desc (numeric on the baht string), unmapped (`ap_name === null`) always last (§4.2). */
export function sortBrandRows<T extends { ap_name?: string | null; gmv: string }>(
  rows: readonly T[],
): T[] {
  const unmapped = (r: T): number => (r.ap_name === null || r.ap_name === undefined ? 1 : 0);
  return [...rows].sort(
    (a, b) => unmapped(a) - unmapped(b) || parseMoneyAmount(b.gmv) - parseMoneyAmount(a.gmv),
  );
}

/**
 * Pulls the filename out of a `Content-Disposition` header (e.g.
 * `attachment; filename="foo.csv"` or `attachment; filename*=utf-8''%E0...`).
 * Per RFC 6266/5987, `filename*` takes priority over the plain `filename`
 * when both are present, and its value is `<charset>''<percent-encoded>` —
 * the BE (Go's `mime.FormatMediaType`) emits it for any non-ASCII name.
 * Returns undefined when the header is absent or carries no usable
 * filename — the caller falls back to a client-built name (Task 2 adds the
 * rest of this file).
 */
export function filenameFromDisposition(header: string | null): string | undefined {
  if (!header) return undefined;

  const extended = /filename\*\s*=\s*([^;]+)/i.exec(header)?.[1]?.trim();
  if (extended) {
    // <charset>''<percent-encoded-value>, e.g. utf-8''%E0%B8...
    const encoded = extended.replace(/^[^']*''/, "");
    try {
      return decodeURIComponent(encoded);
    } catch {
      // Malformed percent-encoding — fall through to the plain form/fallback.
    }
  }

  const plain = /(?:^|;)\s*filename\s*=\s*"?([^";]+)"?/i.exec(header)?.[1];
  return plain ?? undefined;
}
